import { Box, ButtonIcon, Inline, Text } from 'braid-design-system';
import { useId, type ComponentProps } from 'react';

const propsForTone = (
  tone: 'primary' | 'secondary',
): {
  box: Partial<ComponentProps<typeof Box>>;
  text: Partial<ComponentProps<typeof Text>>;
  buttonIcon: Partial<ComponentProps<typeof ButtonIcon>>;
} => {
  switch (tone) {
    case 'primary':
      return {
        buttonIcon: { tone: 'formAccent' },
        box: { boxShadow: 'borderFormAccentLight' },
        text: { tone: 'info' },
      };
    case 'secondary':
      return {
        buttonIcon: { tone: 'neutral' },
        box: { boxShadow: 'borderNeutralLight' },
        text: { tone: 'secondary' },
      };
  }
};

export const LightTag = ({
  onClick,
  onClickIcon,
  iconLabel,
  tone = 'primary',
  icon,
  iconPosition = 'trailing',
  expanded,
  children,
}: {
  onClick?: (() => void) | undefined;
  onClickIcon?: (() => void) | undefined;
  iconLabel?: string;
  tone?: 'primary' | 'secondary';
  icon?: ComponentProps<typeof ButtonIcon>['icon'];
  iconPosition?: 'leading' | 'trailing';
  /** Exposed as `aria-expanded` when the tag controls collapsible content. */
  expanded?: boolean;
  children: string;
}) => {
  const toneProps = propsForTone(tone);
  const buttonIconId = useId();

  const wrappedIcon =
    !icon || !onClickIcon || !iconLabel ? (
      <>
        {icon ? (
          <Text {...toneProps.text} size="small" baseline={false}>
            {icon}
          </Text>
        ) : null}
      </>
    ) : (
      <ButtonIcon
        {...toneProps.buttonIcon}
        id={buttonIconId}
        icon={icon}
        label={iconLabel}
        size="small"
        variant="transparent"
        onClick={(e) => {
          e.stopPropagation();
          onClickIcon();
        }}
      />
    );

  return (
    <Box
      {...toneProps.box}
      component={onClick ? 'button' : 'div'}
      borderRadius="full"
      cursor={onClick ? 'pointer' : undefined}
      onClick={onClick}
      aria-expanded={expanded}
      paddingX="xsmall"
      paddingY="xxsmall"
      transform={onClick ? { active: 'touchable' } : {}}
    >
      <Inline space="xxsmall" alignY="center">
        {iconPosition === 'leading' ? wrappedIcon : null}
        <Text {...toneProps.text} size="small" baseline={false} maxLines={1}>
          {children}
        </Text>
        {iconPosition === 'trailing' ? wrappedIcon : null}
      </Inline>
    </Box>
  );
};
